import { Badge, Col, FormCheck, FormGroup, FormLabel, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useBranch } from "../../../../context/BranchContext";
import BranchSelect from "../../../components/Custom/Main/Branch/BranchSelect";
import { useEffect, useState } from "react";
import InputText from "../../../components/Forms/Element/InputText";
import InputDate from "../../../components/Forms/Element/InputDate";
import PayableReceivableCashFlowTypeSelect from "../../../components/Custom/CashFlow/PayableReceivable/PayableReceivableCashFlowTypeSelect";
import PaymentMethodSelect from "../../../components/Custom/Common/PaymentMethod/PaymentMethodSelect";
import { parseYYYMMDDToDate } from "../../../../utils/DateUtils";
import PayableReceivableCashFlowType from "../../../components/Custom/CashFlow/PayableReceivable/PayableReceivableCashFlowType";
import CategorySelect from "../../../components/Custom/CashFlow/Category/CategorySelect";
import { formatDreGroup } from "../../../components/Custom/CashFlow/Category/DreGroupFormat";
import InputMoney from "../../../components/Forms/Element/InputMoney";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import FormSection from "../../../components/Common/Form/FormSection";
import HintTooltip from "../../../components/Common/HintTooltip";
import CustomSelect from "../../../components/Forms/Element/CustomSelect";

const Form = (props) => {

    const { t } = useTranslation();
    const { formik, entityData, resetForm } = props;
    const { selectedBranch } = useBranch();
    const [paymentDateText, setPaymentDateText] = useState(null);
    const [paidText, setPaidText] = useState(null);

    // Desablita o campo quando for selecionado uma filial no contexto.
    // Se tiver marcado como todas, habilita e exibe todas as filiais para que o usuário escolha.
    const [disabledBranch, setDisabledBranch] = useState(false);
    const selectedDreGroup = formik.values.category?.dreGroup;
    const selectedDreGroupLabel = formik.values.category?.dreGroupLabel
        || formatDreGroup(selectedDreGroup);

    useEffect(() => {
        if (selectedBranch === null) {
            setDisabledBranch(false);
            formik.setFieldValue('branch', null);
        } else {
            // Deixa marcado a filial que está no contexto.
            formik.setFieldValue('branch', {
                label: selectedBranch.name,
                value: selectedBranch.id
            });
            setDisabledBranch(true);
        }
    }, [selectedBranch, resetForm]);

    useEffect(() => {
        if (entityData) {
            formik.setFieldValue('description', entityData.description);
            formik.setFieldValue('value', entityData.value);
            formik.setFieldValue('dueDate', parseYYYMMDDToDate(entityData.dueDate));
            formik.setFieldValue('type', {
                label: entityData.type === PayableReceivableCashFlowType.PAYABLE ? t('cash_flow.payable') : t('cash_flow.receivable'),
                value: entityData.type
            });
            formik.setFieldValue('branch', {
                label: entityData.branch.name,
                value: entityData.branch.id
            });

            formik.setFieldValue('paid', entityData.paid);
            if (entityData.paid) {
                formik.setFieldValue('paymentDate', parseYYYMMDDToDate(entityData.paymentDate));
                formik.setFieldValue('paymentMethod', {
                    label: t(entityData.paymentMethod.toLowerCase()),
                    value: entityData.paymentMethod
                });
            }

            if (entityData.category) {
                formik.setFieldValue('category', {
                    label: entityData.category.name,
                    value: entityData.category.id,
                    dreGroup: entityData.category.dreGroup,
                    dreGroupLabel: formatDreGroup(entityData.category.dreGroup)
                });
            }
        }
    }, [entityData]);

    useEffect(() => {
        if (formik.values.type && formik.values.type.value === PayableReceivableCashFlowType.RECEIVABLE) {
            setPaidText(t('cash_flow.received'));
            setPaymentDateText(t('cash_flow.received_date'));
        } else {
            setPaidText(t('cash_flow.paid'));
            setPaymentDateText(t('cash_flow.payment_date'));
        }
    }, [formik.values.type]);

    const accountStatus = (
        <div className="d-flex flex-column align-items-start align-items-md-end gap-2">
            {formik.values.type && (
                <Badge bg={formik.values.type.value === 'PAYABLE' ? 'danger' : 'success'} className="px-3 py-2">
                    <i className={`fas fa-${formik.values.type.value === 'PAYABLE' ? 'arrow-down' : 'arrow-up'} me-1`}></i>
                    {formik.values.type.value === 'PAYABLE' ? t('cash_flow.expense') : t('cash_flow.income')}
                </Badge>
            )}
            {formik.values.paid && (
                <Badge bg="success" className="px-3 py-2">
                    <i className="fas fa-check-circle me-1"></i>
                    {formik.values.type?.value === 'RECEIVABLE' ? t('cash_flow.received') : t('cash_flow.paid')}
                </Badge>
            )}
        </div>
    );

    return (
        <>
            <div className="form-layout form-layout--spaced">
                <FormSection
                    title={formik.values.type
                        ? (formik.values.type.value === 'PAYABLE' ? t('cash_flow.payable') : t('cash_flow.receivable'))
                        : t('cash_flow.accounts_payable_receivable')}
                    description={t('cash_flow.payable_receivable_information_hint')}
                    icon="fa-solid fa-file-invoice-dollar"
                    actions={accountStatus}
                >

                    <Row className="g-3">
                        <Col md={8}>
                            <FormLabel>{t('description')} <AsteriskRequired/></FormLabel>
                            <InputText
                                name="description"
                                maxLength={50}
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.errors.description}
                            />
                        </Col>
                        <Col md={2}>
                            <FormGroup>
                                <FormLabel>{t('value')} <AsteriskRequired/></FormLabel>
                                <InputMoney
                                    name="value"
                                    value={formik.values.value}
                                    // Contas financeiras sempre exibem centavos, inclusive quando
                                    // o valor é inteiro, para deixar claro o formato monetário aceito.
                                    fixedDecimalScale={true}
                                    onChange={(value) => formik.setFieldValue('value', value)}
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.value} />
                            </FormGroup>
                        </Col>
                        <Col md={2}>
                            <FormLabel>{t('due_date')} <AsteriskRequired/></FormLabel>
                            <InputDate
                                name="dueDate"
                                selected={formik.values.dueDate}
                                onChange={(value) =>
                                    formik.setFieldValue('dueDate', value)
                                }
                                onBlur={formik.handleBlur}
                                error={formik.errors.dueDate}
                            />
                        </Col>
                    </Row>

                    <Row className="g-3 mt-0">
                        <Col md={4} className="order-md-3">
                            <FormLabel>
                                {t('category')}
                                <HintTooltip text={t('cash_flow.payable_receivable_category_hint')} />
                            </FormLabel>
                            <CategorySelect
                                name="category"
                                value={formik.values.category}
                                onChange={(value) =>
                                    formik.setFieldValue('category', value)
                                }
                                onBlur={formik.handleBlur} />
                            {selectedDreGroupLabel && (
                                <small className="text-muted d-block mt-1">
                                    {t('cash_flow.dre_group')}: <strong>{selectedDreGroupLabel}</strong>
                                </small>
                            )}
                            {formik.errors.category && (
                                <small className="text-danger d-block mt-1">{formik.errors.category}</small>
                            )}
                        </Col>
                        <Col md={4} className="order-md-2">
                            <FormGroup>
                                <FormLabel>
                                    {t('type')} <AsteriskRequired />
                                    <HintTooltip text={t('cash_flow.payable_receivable_type_hint')} />
                                </FormLabel>
                                <PayableReceivableCashFlowTypeSelect
                                    onChange={(value) => {
                                        formik.setFieldValue('type', value)
                                    }}
                                    value={formik.values.type}
                                    onBlur={formik.handleBlur}
                                    error={formik.errors.type}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4} className="order-md-1">
                            <FormLabel>{t('branch')} <AsteriskRequired/></FormLabel>
                            <BranchSelect
                                name="branch"
                                value={formik.values.branch}
                                onChange={(value) =>
                                    formik.setFieldValue('branch', value)
                                }
                                onBlur={formik.handleBlur}
                                error={formik.errors.branch}
                                isDisabled={disabledBranch} />
                        </Col>
                    </Row>
                </FormSection>

                <FormSection
                    compact
                    title={t('cash_flow.payable_receivable_payment_condition')}
                    description={t('cash_flow.payable_receivable_settlement_hint')}
                    icon="fa-solid fa-hand-holding-dollar"
                >

                    <Row className="g-3 align-items-end">
                        <Col md={12}>
                            <FormCheck>
                                <input className="form-check-input" type="checkbox" id="repeat-launch"
                                    checked={Boolean(formik.values.recurrenceEnabled)}
                                    onChange={(event) => formik.setFieldValue('recurrenceEnabled', event.target.checked)} />
                                <FormLabel htmlFor="repeat-launch" className="form-check-label">Repetir lançamento? <HintTooltip text="Cria lançamentos futuros com os mesmos dados e vencimentos calculados." /></FormLabel>
                            </FormCheck>
                        </Col>
                        <Col md={3} hidden={!formik.values.recurrenceEnabled}>
                            <FormLabel>Número de recorrências <HintTooltip text="Quantidade total de lançamentos, incluindo o original." /></FormLabel>
                            <InputText type="number" min="2" name="recurrenceCount" value={formik.values.recurrenceCount}
                                onChange={formik.handleChange} />
                        </Col>
                        <Col md={2} hidden={!formik.values.recurrenceEnabled}>
                            <FormLabel>A cada <HintTooltip text="Intervalo entre os lançamentos. Por exemplo: 2 a cada 2 meses." /></FormLabel>
                            <InputText type="number" min="1" name="recurrenceEvery" value={formik.values.recurrenceEvery}
                                onChange={formik.handleChange} />
                        </Col>
                        <Col md={3} hidden={!formik.values.recurrenceEnabled}>
                            <FormLabel>Frequência de pagamento <HintTooltip text="Define se o intervalo será contado em dias, semanas, meses ou anos." /></FormLabel>
                            <CustomSelect
                                options={[
                                    { value: 'MONTH', label: 'Mensal' },
                                    { value: 'WEEK', label: 'Semanal' },
                                    { value: 'DAY', label: 'Diária' },
                                    { value: 'YEAR', label: 'Anual' }
                                ]}
                                value={formik.values.recurrenceFrequency}
                                onChange={(value) => formik.setFieldValue('recurrenceFrequency', value)}
                                isClearable={false}
                                placeholder="Selecione"
                            />
                        </Col>
                        <Col md={12}>
                            <FormCheck>
                                <input
                                    id="paid"
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={formik.values.paid}
                                    onClick={() => {
                                        formik.setFieldValue('paid', !formik.values.paid)
                                    }}
                                />
                                <FormLabel
                                    htmlFor="paid"
                                    className="form-check-label"
                                >
                                    <strong>{!paidText ? t('cash_flow.paid') : paidText}</strong>
                                    <HintTooltip text="Marque quando a conta já tiver sido paga ou recebida." />
                                </FormLabel>
                            </FormCheck>
                        </Col>
                        <Col md={3} hidden={!formik.values.paid}>
                            <FormLabel>{paymentDateText} <HintTooltip text="Data em que o pagamento foi realizado ou o recebimento foi confirmado." /></FormLabel>
                            <InputDate
                                name="paymentDate"
                                selected={formik.values.paymentDate}
                                onChange={(value) =>
                                    formik.setFieldValue('paymentDate', value)
                                }
                                onBlur={formik.handleBlur}
                                error={formik.errors.paymentDate}
                            />
                        </Col>
                        <Col md={3} hidden={!formik.values.paid}>
                            <FormLabel>{t('payment_method')} <HintTooltip text="Forma utilizada para pagar ou receber esta conta." /></FormLabel>
                            <PaymentMethodSelect
                                name="paymentMethod"
                                value={formik.values.paymentMethod}
                                onChange={(value) =>
                                    formik.setFieldValue('paymentMethod', value)
                                }
                                onBlur={formik.handleBlur}
                                error={formik.errors.paymentMethod}
                            />
                        </Col>
                    </Row>
                </FormSection>
            </div>
        </>
    );
}

export default Form;
